import React, { useState } from 'react';
import Select from 'react-select';
import { SelectOption } from 'lib/components/types';
import { useWindowResize } from 'hooks/useWindowResize';
import { Typography } from 'lib/components';
import classes from './index.module.scss';

/**
 * Enum representing the different types of admin users
 * Used for role-based access control in the admin system
 */
enum AdminType {
	SuperAdmin = 1, // Has access to all system features
	Admin = 2, // Has limited access based on permissions
}

/**
 * Props for the AdminTypeSelect component
 */
interface Props {
	value?: AdminType; // Currently selected admin type
	setValue: React.Dispatch<React.SetStateAction<number | undefined>>; // Callback to update selected value
	style?: React.CSSProperties; // Optional custom styles
}

/**
 * Options for the admin type dropdown
 * Maps enum values to human-readable labels
 */
const options: SelectOption<AdminType>[] = [
	{ label: 'Super Administrator', value: 1 },
	{ label: 'Administrator', value: 2 },
];

/**
 * Component for selecting admin type in admin creation/editing forms
 * Provides a dropdown to choose between SuperAdmin and Admin roles
 */
const AdminTypeSelect = ({ value, setValue, style }: Props) => {
	// Get device size for responsive design
	const { isSmallerDevice } = useWindowResize();

	// Track the selected option state
	// Initialize from value prop if provided
	const [option, setOption] = useState<SelectOption<AdminType> | null>(
		typeof value !== undefined ? options.find((option) => option.value === value)! : null
	);

	/**
	 * Handle selection change in the dropdown
	 * Updates both local state and parent component state
	 *
	 * @param option - The newly selected option or null if cleared
	 */
	const handleChange = (option: SelectOption<AdminType> | null) => {
		setOption(option);
		setValue(option!.value);
	};

	return (
		<div className={classes['textfield-container']} style={style}>
			<Typography color="darkgrey" variant={isSmallerDevice ? 'h5' : 'subheading2'}>
				Role:
			</Typography>
			<Select
				className={classes['react-select-container']}
				classNamePrefix="react-select"
				options={options}
				value={option}
				onChange={handleChange}
			/>
		</div>
	);
};

export default AdminTypeSelect;
